import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, X, Loader2 } from 'lucide-react';
import { SCOUT_POINT_VALUES } from '@tendedloop/shared';
import {
  compressImage,
  getImagePreviewUrl,
  formatFileSize,
  isValidImageType,
  MAX_FILE_SIZE,
} from '@/lib/imageCompression';

interface PhotoCaptureProps {
  onCapture: (file: File) => void;
  capturedImage: File | null;
  onRemove: () => void;
  disabled?: boolean;
}

export default function PhotoCapture({ onCapture, capturedImage, onRemove, disabled = false }: PhotoCaptureProps) {
  const { t } = useTranslation('scout');
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (capturedImage) {
      getImagePreviewUrl(capturedImage)
        .then(setPreviewUrl)
        .catch(() => setPreviewUrl(null));
    } else {
      setPreviewUrl(null);
    }
  }, [capturedImage]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    setError(null);

    if (!isValidImageType(file)) {
      setError(t('photo.invalidType'));
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError(t('photo.tooLarge'));
      return;
    }

    setIsProcessing(true);
    try {
      const compressedFile = await compressImage(file);
      onCapture(compressedFile);
    } catch {
      setError(t('photo.processingFailed'));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        onChange={handleFileSelect}
        disabled={disabled || isProcessing}
        className="hidden"
      />

      {capturedImage && previewUrl ? (
        <div className="relative">
          <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-warm-border bg-muted">
            <img src={previewUrl} alt={t('photo.preview')} className="h-full w-full object-cover" />
          </div>
          <p className="mt-1 text-caption text-muted-foreground text-center">{formatFileSize(capturedImage.size)}</p>
          <button
            type="button"
            onClick={() => {
              setPreviewUrl(null);
              setError(null);
              onRemove();
            }}
            disabled={disabled}
            className="absolute -top-2 -end-2 w-8 h-8 rounded-full bg-critical text-white flex items-center justify-center shadow-md"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || isProcessing}
          className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-warm-border px-4 py-4 transition-colors hover:bg-warm-faint disabled:opacity-50"
        >
          {isProcessing ? (
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          ) : (
            <>
              <Camera className="w-5 h-5 text-muted-foreground" />
              <span className="text-small text-muted-foreground">{t('photo.addPhoto')}</span>
              <span className="text-caption text-gold font-medium">
                {t('feedback.xpEarned', { xp: SCOUT_POINT_VALUES.PHOTO })}
              </span>
            </>
          )}
        </button>
      )}

      {error && <p className="text-caption text-critical">{error}</p>}
    </div>
  );
}
